import type { FindByTargetIdsResponse, RuleViolation } from './rule-violations'

export interface RuleViolationsBatcherOptions {
  /** Time window in ms during which target ids are accumulated into one request. */
  windowMs?: number
  /** How long a target id's result is cached after a successful fetch. */
  cacheTtlMs?: number
  /** Maximum cache entries; oldest is evicted FIFO when exceeded. */
  cacheMaxEntries?: number
}

interface CacheEntry {
  violations: RuleViolation[]
  expires: number
}

interface Waiter {
  resolve: (value: RuleViolation[]) => void
  reject: (error: unknown) => void
}

export type RuleViolationsBatchFetcher = (
  targetIds: string[],
) => Promise<FindByTargetIdsResponse>

/**
 * Coalesces concurrent `findByTargetIds` lookups across hook instances into a
 * single POST per window, with a short-lived per-id cache. Mirrors the
 * batching pattern used by package-plan's conflictBatcher so framework
 * consumers get the same network savings without re-implementing it.
 */
export class RuleViolationsBatcher {
  private readonly windowMs: number
  private readonly cacheTtlMs: number
  private readonly cacheMaxEntries: number

  private readonly cache = new Map<string, CacheEntry>()
  private queuedIds = new Set<string>()
  private waiters = new Map<string, Waiter[]>()
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private readonly fetcher: RuleViolationsBatchFetcher,
    options: RuleViolationsBatcherOptions = {},
  ) {
    this.windowMs = options.windowMs ?? 50
    this.cacheTtlMs = options.cacheTtlMs ?? 30_000
    this.cacheMaxEntries = options.cacheMaxEntries ?? 500
  }

  fetchByTargetId(targetId: string): Promise<RuleViolation[]> {
    const cached = this.readCache(targetId)
    if (cached !== null) return Promise.resolve(cached)

    return new Promise((resolve, reject) => {
      const existing = this.waiters.get(targetId)
      if (existing) {
        existing.push({ resolve, reject })
      } else {
        this.waiters.set(targetId, [{ resolve, reject }])
      }
      this.queuedIds.add(targetId)
      if (this.timer === null) {
        this.timer = setTimeout(() => this.flush(), this.windowMs)
      }
    })
  }

  invalidate(targetId?: string): void {
    if (targetId === undefined) {
      this.cache.clear()
      return
    }
    this.cache.delete(targetId)
  }

  private readCache(targetId: string): RuleViolation[] | null {
    const entry = this.cache.get(targetId)
    if (!entry) return null
    if (entry.expires <= Date.now()) {
      this.cache.delete(targetId)
      return null
    }
    return [...entry.violations]
  }

  private writeCache(targetId: string, violations: RuleViolation[]): void {
    this.cache.set(targetId, {
      violations: [...violations],
      expires: Date.now() + this.cacheTtlMs,
    })
    if (this.cache.size > this.cacheMaxEntries) {
      const oldest = this.cache.keys().next().value
      if (oldest !== undefined) this.cache.delete(oldest)
    }
  }

  private async flush(): Promise<void> {
    const idsToFetch = Array.from(this.queuedIds)
    const currentWaiters = this.waiters
    this.queuedIds = new Set()
    this.waiters = new Map()
    this.timer = null

    try {
      const response = await this.fetcher(idsToFetch)
      const bucketed = bucketByTargetId(idsToFetch, response.violations ?? [])
      for (const id of idsToFetch) {
        const violations = bucketed[id]
        this.writeCache(id, violations)
        currentWaiters
          .get(id)
          ?.forEach((waiter) => waiter.resolve([...violations]))
      }
    } catch (err) {
      for (const id of idsToFetch) {
        currentWaiters.get(id)?.forEach((waiter) => waiter.reject(err))
      }
    }
  }
}

const bucketByTargetId = (
  ids: string[],
  violations: RuleViolation[],
): Record<string, RuleViolation[]> => {
  const map: Record<string, RuleViolation[]> = {}
  for (const id of ids) map[id] = []
  for (const violation of violations) {
    const bucket = map[violation.targetId]
    if (bucket) bucket.push(violation)
  }
  return map
}
